import { Container } from '@cluey/cluey-components';
import moment from 'moment';
import { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';

import { api } from '../../../../../../api';
import { useMultiStep } from '../../../../../common/multi-step';
import { submitCaseToSF, submitChangePause, submitNewPause } from '../../../../../legacy/actions';
import { Loader } from '../../../../../legacy/components';
import { PauseEnrolmentContext } from '../../../../pause-enrolment/PauseEnrolmentContext';
import PauseEnrolmentForm from '../../../../pause-enrolment/form-steps/PauseEnrolmentForm/PauseEnrolmentForm';
import { enrolmentWillBePaused } from '../../../../pause-enrolment/form-steps/PauseEnrolmentForm/helpers';
import { createPauseMethodsForCancelPromo } from '../../createPauseMethodsForCancelPromo';
import { createPauseResumptionOptionsForCancelPromo } from '../../createPauseResumptionOptionsForCancelPromo';
import SummerPauseAndDiscountInfoBox from './SummerPauseAndDiscountInfoBox';

export type SelectPauseDateProps = {
  handleCancelEnrolment: (onSubmitSuccess: () => void, onSubmitFailed: () => void) => void;
};

const SelectPauseDates = ({ handleCancelEnrolment }: SelectPauseDateProps) => {
  const context = useContext(PauseEnrolmentContext);
  const { selectedEnrolment } = context;
  const { goToStep } = useMultiStep();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const dispatch = useDispatch();

  const willBePaused = enrolmentWillBePaused(selectedEnrolment);

  if (submitting) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }
  const continueToCancelEnrolment = () => {
    setSubmitting(true);
    handleCancelEnrolment(
      () => {
        setSubmitting(false);
        goToStep('cancel-enrolment-success');
      },
      () => {
        setSubmitError('There was a problem with submitting the request. Please try again later.');
        setSubmitting(false);
      }
    );
  };
  const handleSubmit = ({ pauseStartDate, resumptionDate }) => {
    setSubmitError(null);
    setSubmitting(true);
    const pauseEndDate = moment(resumptionDate).subtract(1, 'day');
    if (willBePaused) {
      const payload = {
        enrolmentId: selectedEnrolment.enrolmentSfid,
        currentPauseStart: moment(selectedEnrolment.enrolmentPausedFrom).format('YYYY-MM-DD'),
        selectedResumeDate: pauseEndDate.format('YYYY-MM-DD'),
        doNotOffsetEndDate: true,
        callback: handleOnSubmitSuccess,
        errorCallback: handleOnSubmitFailed,
      };
      dispatch(submitChangePause(payload));
    } else {
      const payload = {
        enrolmentId: selectedEnrolment.enrolmentSfid,
        startDate: moment(pauseStartDate).format('YYYY-MM-DD'),
        endDate: pauseEndDate.format('YYYY-MM-DD'),
        onSubmitSuccess: handleOnSubmitSuccess,
        onSubmitFailed: handleOnSubmitFailed,
      };
      dispatch(submitNewPause(payload));
    }
  };

  const handleOnSubmitSuccess = () => {
    handleAcceptOffer();
  };

  const handleOnSubmitFailed = (error: Error) => {
    setSubmitError(error.message);
    setSubmitting(false);
  };

  const handleAcceptOffer = () => {
    setSubmitting(true);
    const payload = {
      caseRecordType: 'Support',
      caseRequestFrom: 'Parent',
      caseType: 'Payment',
      caseCategory: 'Support Request',
      caseSubject: 'Apply resumption offer',
      caseSubCategory: 'Additional discount requested',
      message:
        'The customer has opted to pause over summer rather than cancel. Please apply 20% off their next 5 unpaid sessions. Please ensure the pause has gone through first i.e. next unpaid session date is Term 1 2026.',
      uniqueEnrolmentId: selectedEnrolment.enrolmentId,
      onSubmitSuccess: () => {
        api.salesforceCase.all.invalidateQueries();
        setSubmitting(false);
        goToStep('summer-pause-and-discount-success');
      },
      onSubmitFailed: (error) => {
        console.log('[error] submit error ', { error });
        setSubmitting(false);
        setSubmitError(
          'There was a problem with submitting the request to apply the discount. Please try again later.'
        );
      },
    };
    dispatch(submitCaseToSF(payload));
  };

  return (
    <Container>
      <SummerPauseAndDiscountInfoBox handleCancelEnrolment={continueToCancelEnrolment} />
      <PauseEnrolmentForm
        submitError={submitError}
        submit={handleSubmit}
        overrideCreatePauseMethods={createPauseMethodsForCancelPromo}
        overrideCreatePauseResumptionOptions={createPauseResumptionOptionsForCancelPromo}
      />
    </Container>
  );
};

export default SelectPauseDates;
